import apiClient from "../lib/api-client";

export interface Hospital {
  id: string;
  tenantId: string;
  hospitalCode: string;
  hospitalName: string;
  facilityType:
    | "HOSPITAL"
    | "CLINIC"
    | "NURSING_HOME"
    | "DIAGNOSTIC_CENTER"
    | "PHC"
    | "CHC"
    | "PHARMACY"
    | "OTHER";
  registrationNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  phone?: string;
  email?: string;
  bedCapacity?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    doctors: number;
    invoices: number;
    prescriptions: number;
  };
}

export interface CreateHospitalDto {
  hospitalCode: string;
  hospitalName: string;
  facilityType?: string;
  registrationNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  phone?: string;
  email?: string;
  bedCapacity?: number;
}

export interface UpdateHospitalDto extends Partial<CreateHospitalDto> {
  isActive?: boolean;
}

export interface HospitalListResponse {
  data: Hospital[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const hospitalService = {
  async getAll(params?: {
    page?: number;
    limit?: number;
    search?: string;
    facilityType?: string;
    isActive?: boolean;
  }): Promise<HospitalListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append("page", params.page.toString());
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.search) searchParams.append("search", params.search);
    if (params?.facilityType)
      searchParams.append("facilityType", params.facilityType);
    if (params?.isActive !== undefined)
      searchParams.append("isActive", params.isActive.toString());

    const response = await apiClient.get(`/hospitals?${searchParams}`);
    return response.data;
  },

  async getById(id: string): Promise<Hospital> {
    const response = await apiClient.get(`/hospitals/${id}`);
    return response.data;
  },

  async create(data: CreateHospitalDto): Promise<Hospital> {
    const response = await apiClient.post("/hospitals", data, {
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
    return response.data;
  },

  async update(id: string, data: UpdateHospitalDto): Promise<Hospital> {
    const response = await apiClient.patch(`/hospitals/${id}`, data, {
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/hospitals/${id}`, {
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
  },

  async search(query: string, limit?: number): Promise<Hospital[]> {
    const searchParams = new URLSearchParams();
    searchParams.append("q", query);
    if (limit) searchParams.append("limit", limit.toString());

    const response = await apiClient.get(`/hospitals/search?${searchParams}`);
    return response.data;
  },
};
